import { Component } from '@theme/component';

/**
 * Drives the filter pills + search box in sections/store-locations-directory.liquid.
 * Toggles `hidden` on cards/city groups (blocks/store-locations-city.liquid,
 * blocks/store-locations-store.liquid) and notifies each city group so its
 * carousel (store-city-carousel.js) can reset to a visible card.
 *
 * The per-city pills themselves are built here, not in Liquid: the "Cidade"
 * blocks are dynamically-rendered theme blocks (content_for 'blocks', so
 * merchants can add/reorder/remove them), and dynamically-rendered blocks
 * can't be looped over or rendered conditionally from other Liquid — only
 * content_for can render them. So this reads the city groups already in the
 * DOM and builds one pill per city from that.
 */
class StoreLocationsDirectory extends Component {
  activeCity = 'all';
  searchTerm = '';

  connectedCallback() {
    super.connectedCallback();
    this.#buildCityPills();
  }

  #buildCityPills() {
    const container = this.refs.pillsContainer;
    if (!container) return;

    let total = 0;

    for (const cityEl of this.refs.cityGroups ?? []) {
      const city = cityEl.dataset.city;
      if (!city) continue;

      const count = cityEl.querySelectorAll('.store-locations-city__card').length;
      total += count;
      container.append(this.#createPill(city, count));
    }

    if (this.refs.allCount) this.refs.allCount.textContent = String(total);
  }

  /**
   * @param {string} city
   * @param {number} count
   */
  #createPill(city, count) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'store-locations-directory__pill';
    button.dataset.city = city;
    button.setAttribute('aria-pressed', 'false');
    button.append(`${city} `);

    const badge = document.createElement('span');
    badge.className = 'store-locations-directory__pill-count';
    badge.textContent = String(count);
    button.append(badge);

    button.addEventListener('click', () => this.filterCity({ city }));

    return button;
  }

  /** @param {{ city: string }} data */
  filterCity(data) {
    this.activeCity = String(data.city);

    for (const pill of this.querySelectorAll('.store-locations-directory__pill')) {
      const isActive = /** @type {HTMLElement} */ (pill).dataset.city === this.activeCity;
      pill.classList.toggle('is-active', isActive);
      pill.setAttribute('aria-pressed', String(isActive));
    }

    this.#applyFilters();
  }

  /** @param {Event} event */
  search(event) {
    const target = /** @type {HTMLInputElement} */ (event.target);
    this.searchTerm = target.value.trim().toLowerCase();
    this.#applyFilters();
  }

  #applyFilters() {
    let totalVisible = 0;

    for (const cityEl of this.refs.cityGroups ?? []) {
      const cityMatches = this.activeCity === 'all' || cityEl.dataset.city === this.activeCity;
      let visibleCards = 0;

      for (const card of cityEl.querySelectorAll('[data-search-text]')) {
        const searchText = /** @type {HTMLElement} */ (card).dataset.searchText ?? '';
        const show = cityMatches && (!this.searchTerm || searchText.includes(this.searchTerm));
        /** @type {HTMLElement} */ (card).hidden = !show;
        if (show) visibleCards++;
      }

      cityEl.hidden = visibleCards === 0;
      totalVisible += visibleCards;
      cityEl.dispatchEvent(new CustomEvent('storelocations:cardsfiltered', { bubbles: false }));
    }

    if (this.refs.emptyState) this.refs.emptyState.hidden = totalVisible > 0;
  }
}

customElements.define('store-locations-directory-component', StoreLocationsDirectory);
